import type { Router } from 'vue-router';
import { t } from '../i18n';
import { useNotice } from '../composables/useDialog';

// Recovery for the stale-tab-after-deploy trap, installed once per app in
// main.ts (`installChunkReloadRecovery(router)`).
//
// Vite builds content-hashed chunks, so every deploy retires the asset names an
// already-open tab knows. The next lazy route's import() 404s, vue-router aborts
// the navigation, and because a router.push() rejection is routinely unhandled
// the app appears to do NOTHING: same URL, same screen, no error. The sysadmin
// console showed the worst face of it — a login that answered 200, stored the
// token, and then left the form sitting there, which reads as "login is broken".
//
// The repair is a full document load of the route the person asked for: the UI
// images serve index.html with `no-store`, so that picks up the new manifest and
// its chunk names. The sessionStorage flag makes it strictly ONE attempt, so a
// chunk that is genuinely gone (a half-finished rollout, a broken build) ends in
// a notice rather than a tab reloading itself forever. Where the flag cannot be
// written at all, we do not reload: an unrememberable attempt is a loop.
const RELOAD_FLAG = 'lumi:chunk-reload-attempted';

// Deliberate string matching. Browsers disagree on both the error type and the
// wording, and none exposes a stable code: Chromium says "Failed to fetch
// dynamically imported module", Firefox "error loading dynamically imported
// module", Safari "Importing a module script failed".
function isChunkLoadError(error: unknown): boolean {
  const message = String((error as { message?: unknown })?.message ?? error ?? '').toLowerCase();
  if (!message) return false;
  return (
    message.includes('dynamically imported module') ||
    message.includes('importing a module script failed') ||
    message.includes('error loading dynamically imported module') ||
    /loading (css )?chunk \S+ failed/.test(message)
  );
}

function sessionStore(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function explainToUser() {
  useNotice().error({ title: t('errors.appUpdatedTitle'), body: t('errors.appUpdatedBody') });
}

export function installChunkReloadRecovery(router: Router): void {
  router.onError((error, to) => {
    if (!isChunkLoadError(error)) return;

    const store = sessionStore();
    if (!store || store.getItem(RELOAD_FLAG)) {
      explainToUser();
      return;
    }
    try {
      store.setItem(RELOAD_FLAG, '1');
    } catch {
      explainToUser();
      return;
    }
    // The route they asked for, not the one they are stuck on: after a login
    // this lands them where the successful login was taking them.
    window.location.assign(to.fullPath);
  });

  // A completed navigation proves the tab is on the current build again, so the
  // next deploy gets its own single attempt.
  router.afterEach(() => {
    try {
      sessionStore()?.removeItem(RELOAD_FLAG);
    } catch {
      // A tab that cannot clear the flag simply forgoes the next auto-reload.
    }
  });
}
